import { c as _c } from "react/compiler-runtime";
import type { BigIntish } from '@morpho-org/blue-sdk';
import { differenceInSeconds, formatDuration, intervalToDuration, isBefore } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useEffect, useState } from 'react';
export interface CountdownResult {
  formatted: string;
  isBelowOneHour: boolean;
}

/**
 * Hook that provides a live countdown to a target timestamp.
 * Updates every second with formatted duration and urgency indicator.
 * @param validAt - Target timestamp (Unix seconds)
 * @returns Object with formatted countdown string and urgency flag
 */
export const useCountdown = validAt => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      formatted: "",
      isBelowOneHour: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [countdown, setCountdown] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== validAt) {
    t1 = () => {
      const updateCountdown = () => {
        const now = new Date();
        const targetTime = new Date(Number(validAt) * 1000);
        if (!isBefore(now, targetTime)) {
          setCountdown({
            formatted: "Action executable",
            isBelowOneHour: false
          });
          return;
        }
        const duration = intervalToDuration({
          start: now,
          end: targetTime
        });
        const totalSeconds = differenceInSeconds(targetTime, now);
        const isBelowOneHour = totalSeconds < 3600;
        let formatted;
        if (isBelowOneHour) {
          formatted = formatDuration(duration, {
            format: ["minutes", "seconds"],
            locale: enUS
          });
        } else {
          formatted = formatDuration(duration, {
            format: ["days", "hours", "minutes"],
            locale: enUS
          });
        }
        setCountdown({
          formatted,
          isBelowOneHour
        });
      };
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    };
    t2 = [validAt];
    $[1] = validAt;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return countdown;
};